import { Box, Stack, Typography, useTheme, Button, Link } from '@mui/joy';
import { Link as ScrollLink } from 'react-scroll';
import {
	Chart,
	Content,
	PageHeading,
	PageSubTitle,
	PageTitle,
	Section,
	AnalysisHeading,
	OtherPages,
	Divider
} from '../Display';
import { CHAPTERS } from '../../lib/const';
import { ReturnToHomepageButton } from '../Nav/ReturnToHomepageButton';
import SimpleRadialChart from '../Charts/SimpleRadialChart';
import { DataKey } from '../../lib/model';
import SimpleDisaggChart from '../Charts/SimpleDisaggChart';
import SimpleBarChart from '../Charts/SimpleBarChart';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import TableSheet from '../Charts/SimpleTable';
import { DotsIndicator } from '../DotsIndicator/DotsIndicator';
import { Download } from '@mui/icons-material';
import { ChapterPreviewContainer } from '../Chapters/ChapterPreviewContainer';
import AnalysisHighlights from './AnalysisHighlights';
import Caption from '../Display/Caption';
import { SimpleNav } from '../Nav/SimpleNav';
import { contentCopy, data, caption } from '../../lib/staticData';
import PageWrapper from '../PageWrapper';
import { KeyboardArrowRight } from '@mui/icons-material';

const AnalysisPage = () => {
	const theme = useTheme();
	const chapter = 'analysis';

	const matchesMD = useMediaQuery(theme.breakpoints.up(850));
	const matchesLG = useMediaQuery(theme.breakpoints.up(1140));
	const matchesXS = useMediaQuery(theme.breakpoints.up(450));
	const gridLayout = () => {
		if (matchesMD) return '1fr 1fr';
		else return '1fr';
	};
	const scrollMatch = useMediaQuery(theme.breakpoints.up('smallLaptop'));

	return (
		<PageWrapper>
			<Box
				id='topOfPage'
				sx={{
					backgroundColor: theme.palette.background.surface,
					display: 'flex',
					flexDirection: 'column',
					position: 'relative',
					paddingX: { xs: '5px', sm: '30px', lg: '60px' }
				}}
			>
				<Box
					sx={{
						zIndex: 1,
						flexGrow: 1,
						display: 'flex',
						flexDirection: 'column',
						margin: '40px auto 20px',

						width: '100%',
						maxWidth: '1366px'
					}}
				>
					<Content>
						<Box
							sx={{
								display: 'flex',
								flexDirection: { xs: 'column', md: 'row' },
								justifyContent: 'space-between',
								alignItems: { xs: 'revert', md: 'center' },
								gap: '16px',
								marginBottom: { xs: '2rem', md: 0 },
								paddingRight: '1rem'
							}}
						>
							<PageHeading
								bold='2024'
								regular='Women, Money, & Politics Watch'
							/>
						<ReturnToHomepageButton fullWidthOnMobile />
						</Box>

						<PageTitle title={CHAPTERS[chapter].header} />
						<PageSubTitle subTitle={CHAPTERS[chapter].subheader} />
						<Section sx={{ marginTop: '15px' }}>
							<Typography level='body1'>{contentCopy.intro}</Typography>
							<Typography level='body1' sx={{ marginBottom: '1.5rem' }}>
								Source: CAWP and{' '}
								<a
									target='_blank'
									rel='noopener noreferrer'
									href='https://www.opensecrets.org'
								>
									OpenSecrets
								</a>
							</Typography>
						</Section>
					</Content>
					<br />
					<Chart>
						<ChapterPreviewContainer
							chapter={'analysis'}
							splatterBg
							backgroundGradientProps={{
								gradientHeight: 400,
								sx: {
									paddingX: { xs: '15px', md: '44.5px' },
									paddingY: '44.5px',
									maxWidth: '1139px',
									display: 'flex',
									flexDirection: 'row'
								}
							}}
						>
							<Box
								sx={{
									backgroundColor: '#FFFFFF',
									display: 'flex',
									flexDirection: { xs: 'column', md: 'column', lg: 'row' },
									justifyContent: 'space-between',
									gap: '4rem',
									padding: { xs: '10px', sm: '44.5px' },
									boxShadow: '0px 1px 4px #00000029',
									flexGrow: 1
								}}
							>
								<Box
									sx={{
										height: { xs: '100%', md: 410 }
									}}
								>
									<AnalysisHighlights />
								</Box>
								<Stack
									sx={{
										overflow: 'hidden',
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'flex-start',
										alignContent: 'flex-start',
										flexDirection: 'column',
										maxHeight: { xs: '100%', md: 410 },
										maxWidth: 512,
										flexGrow: 1
									}}
								>
									<Box
										sx={{
											'a:hover': {
												textDecoration: `underline 1px ${theme.palette.chartElementsBackground.title}`,
												textUnderlineOffset: '0.15rem'
											}
										}}
									>
										<Typography level='body1'>
											Final Analysis Contents:
										</Typography>
										<Stack
											sx={{
												flexDirection: 'row',
												alignItems: 'center',
												gap: '18px',
												marginTop: '1rem'
											}}
										>
											<DotsIndicator
												chapter={chapter}
												diameter='21px'
												isActive={true}
											/>
											<ScrollLink
												style={{
													cursor: 'pointer'
												}}
												to={'congressional'}
												smooth='easeInOutCubic'
												offset={scrollMatch ? -156 : 0}
												duration={500}
											>
												<Typography
													level='h2'
													sx={{
														fontSize: '1.4375rem',
														lineHeight: '1.875rem',
														fontWeight: 'bold',
														letterSpacing: 0
													}}
												>
													Congressional Elections
												</Typography>
											</ScrollLink>
										</Stack>
										<Stack
											sx={{
												flexDirection: 'row',
												alignItems: 'center',
												gap: '18px',
												marginTop: '1.5rem'
											}}
										>
											<DotsIndicator
												chapter={chapter}
												diameter='21px'
												isActive={true}
											/>

											<ScrollLink
												style={{
													cursor: 'pointer'
												}}
												to={'state'}
												smooth='easeInOutCubic'
												offset={scrollMatch ? -156 : 0}
												duration={500}
											>
												<Typography
													level='h2'
													sx={{
														fontSize: '1.4375rem',
														fontWeight: 'bold',
														letterSpacing: 0
													}}
												>
													State Elections
												</Typography>
											</ScrollLink>
										</Stack>
									</Box>
									<Box
										sx={{
											marginBottom: { xs: '1rem', lg: '2.5rem' },
											marginTop: { xs: '2rem', lg: 0 }
										}}
									>
										<Link
											href={
												'/modules/custom/cawp_wmpw/assets/pdfs/CAWP_Final_Analysis_Congressional_and_State_Elections.pdf'
											}
											sx={{ ':hover': { textDecoration: 'none' } }}
											download='CAWP_Final_Analysis_Congressional_and_State_Elections.pdf'
										>
											<Button
												variant='outlined'
												color='neutral'
												sx={{
													':hover': {
														color: '#FFFFFF',
														'& .MuiSvgIcon-root': {
															color: '#FFFFFF'
														}
													},

													padding: '10px',
													paddingTop: '11px',
													paddingX: '15px',

													color: '#111827',
													fontWeight: 'normal',
													fontSize: '20px',
													border: '1.5px solid #374151',
													'.MuiSvgIcon-root': {
														fontSize: '1.75rem',
														color: '#111827',
														marginRight: '0.25rem'
													}
												}}
											>
												<Download />
												{/* <Typography
											level='label'
											sx={{
												marginLeft: '.5rem',
												fontWeight: 'normal',
												':hover': { color: '#FFFFFF' }
											}}
										> */}
												Download PDF Report
												{/* </Typography> */}
											</Button>
										</Link>
									</Box>
								</Stack>
							</Box>
						</ChapterPreviewContainer>
					</Chart>

					<Content
						sx={{
							paddingLeft: { xs: '16px', md: '9rem' },
							paddingRight: { xs: '16px', md: 0 }
						}}
					>
						<Box sx={{ maxWidth: { xs: '100%', lg: 1050 }, marginTop: '4rem' }}>
							{/* CONGRESSIONAL */}
							<AnalysisHeading
								number='1'
								title='Congressional Elections'
								anchor={'congressional'}
							/>
							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='h4' sx={{ marginTop: '4rem' }}>
									{contentCopy.congress.title1}
								</Typography>
								<Typography level='body3' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.congress.content1A}
								</Typography>
							</Box>
							<Box
								sx={{
									backgroundColor: theme.palette.chartElementsBackground.bg,
									padding: { xs: '1rem .5rem', sm: '1rem 3rem' }
								}}
							>
								<Typography
									level='body3'
									sx={{ textAlign: 'center', marginTop: '1rem' }}
								>
									Challengers
								</Typography>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: gridLayout,
										flexGrow: 1
									}}
									// sx={{
									// 	flexDirection: { xs: 'column', sm: 'row' }
									// }}
								>
									<SimpleRadialChart
										office={'US Senate'}
										topic={DataKey.PropSelf}
										small={!matchesLG}
										chartData={data.congressRadial1}
									/>
									<SimpleRadialChart
										office={'US House'}
										topic={DataKey.PropSelf}
										small={!matchesLG}
										chartData={data.congressRadial2}
									/>
								</Box>
								<Box
									sx={{
										borderTop: '1px solid gray',
										mx: { xs: 0, sm: '-1.5rem' }
									}}
								></Box>
								<Typography
									level='body3'
									sx={{ textAlign: 'center', marginTop: '2rem' }}
								>
									Open Seats
								</Typography>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: gridLayout,
										flexGrow: 1
									}}
								>
									<SimpleRadialChart
										office={'US Senate'}
										topic={DataKey.PropSelf}
										small={!matchesLG}
										chartData={data.congressRadial3}
									/>
									<SimpleRadialChart
										office={'US House'}
										topic={DataKey.PropSelf}
										small={!matchesLG}
										chartData={data.congressRadial4}
									/>
								</Box>
								<Caption
									title={caption.selfFinance.title}
									text={caption.selfFinance.text}
								/>
							</Box>
							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body3' sx={{ margin: '4rem 0 1.5rem 0' }}>
									{contentCopy.congress.content1B}
								</Typography>
							</Box>
							<Box
								sx={{
									backgroundColor: theme.palette.chartElementsBackground.bg,
									padding: { xs: '1rem .5rem', sm: '1rem 3rem' }
								}}
							>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: gridLayout,
										flexGrow: 1
									}}
								>
									<SimpleRadialChart
										office={'US Senate'}
										topic={DataKey.PropSmall}
										small={!matchesLG}
										chartData={data.congressRadial5}
									/>
									<SimpleRadialChart
										office={'US House'}
										topic={DataKey.PropSmall}
										small={!matchesLG}
										chartData={data.congressRadial6}
									/>
								</Box>
								<Caption
									title={caption.contrib.title}
									text={caption.contrib.text}
								/>
							</Box>
							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body3' sx={{ margin: '4rem 0 1.5rem 0' }}>
									{contentCopy.congress.content1C}
								</Typography>
							</Box>
							<Box
								sx={{
									backgroundColor: theme.palette.chartElementsBackground.bg,
									padding: { xs: '1rem .5rem', sm: '1rem 3rem' }
								}}
							>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: gridLayout,
										flexGrow: 1
									}}
								>
									<SimpleDisaggChart
										type='pct'
										office='US House'
										topic={DataKey.PropSmall}
										title='Democratic Incumbents'
										chartData={data.miniBar1}
									/>
									<SimpleDisaggChart
										type='pct'
										office='US House'
										topic={DataKey.PropSmall}
										title='Republican Incumbents'
										chartData={data.miniBar2}
									/>
								</Box>
								<Typography
									sx={{
										padding: '0 2rem 1rem 0',
										backgroundColor: theme.palette.chartElementsBackground.bg,
										fontFamily: 'Source Sans Pro, sans-serif'
									}}
								>
									MENA - Middle Eastern/North African &nbsp; NatAm - Native
									American/Alaska Native/Native Hawaiian &nbsp; AAPI - Asian
									American/Pacific Islander
								</Typography>
								<Caption
									title={caption.contrib.title}
									text={caption.contrib.text}
								/>
							</Box>
							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body3' sx={{ marginTop: '4rem' }}>
									{contentCopy.congress.content1Da}{' '}
									<i>{contentCopy.congress.content1Db}</i>
									<span> {contentCopy.congress.content1Dc}</span>
								</Typography>

								<Stack
									sx={{
										flexDirection: 'row',
										justifyContent: 'center',
										margin: '2rem 0'
									}}
								>
									<DotsIndicator
										chapter={chapter}
										diameter='29px'
										isActive={true}
									/>
								</Stack>
							</Box>
							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='h4' sx={{ marginTop: '4rem' }}>
									{contentCopy.congress.title2}
								</Typography>
								<Typography level='body3' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.congress.content2A}
								</Typography>
							</Box>
							<Stack
								sx={{
									display: matchesXS ? 'none' : 'flex',
									flexDirection: 'row',
									justifyContent: 'flex-end',
									margin: '.5rem 0',
									alignItems: 'center'
								}}
							>
								<Typography>Scroll to see Donation amount</Typography>
								<KeyboardArrowRight
									sx={{
										fontSize: '1.5rem',
										color: theme.typography.body1.color
									}}
								/>
							</Stack>
							<Box
								sx={{
									backgroundColor: theme.palette.chartElementsBackground.bg,
									padding: { xs: '1rem .5rem', sm: '1rem 3rem' }
								}}
							>
								<TableSheet />
							</Box>

							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body3' sx={{ marginTop: '4rem' }}>
									{contentCopy.congress.content2B}
								</Typography>
								<Stack
									sx={{
										flexDirection: 'row',
										justifyContent: 'center',
										margin: '2rem 0'
									}}
								>
									<DotsIndicator
										chapter={chapter}
										diameter='29px'
										isActive={true}
									/>
								</Stack>
							</Box>
							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='h4' sx={{ marginTop: '5rem' }}>
									{contentCopy.congress.title3}
								</Typography>
								<Typography level='body3' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.congress.content3A}
								</Typography>
							</Box>

							<Box
								sx={{
									backgroundColor: theme.palette.chartElementsBackground.bg,
									padding: { xs: '1rem .5rem', sm: '1rem 3rem' }
								}}
							>
								<Typography
									level='body3'
									sx={{ textAlign: 'center', marginTop: '2rem' }}
								>
									Incumbents
								</Typography>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: gridLayout,
										flexGrow: 1
									}}
								>
									<SimpleBarChart
										office='US Senate'
										topic={DataKey.TotalRaised}
										chartData={data.bar1}
										maxValue={data.bar1[0].value} //charts need to be scaled to the same max value and this is the biggest one
									/>
									<SimpleBarChart
										office='US House'
										topic={DataKey.TotalRaised}
										chartData={data.bar2}
										maxValue={data.bar1[0].value}
									/>
								</Box>
								<Caption
									title={caption.totalRaised.title}
									text={caption.totalRaised.text}
								/>
							</Box>
							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body3' sx={{ margin: '4rem 0 1.5rem 0' }}>
									{contentCopy.congress.content3B}
								</Typography>
							</Box>

							<Box
								sx={{
									backgroundColor: theme.palette.chartElementsBackground.bg,
									padding: { xs: '1rem .5rem', sm: '1rem 3rem' }
								}}
							>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: gridLayout,
										flexGrow: 1
									}}
								>
									<SimpleDisaggChart
										type='money'
										office='US House'
										topic={DataKey.TotalRaised}
										title='Democratic Incumbents'
										chartData={data.miniBar3}
										maxValue={data.miniBar3[3].value} //charts need to be scaled to the same max value and this is the biggest one
									/>
									<SimpleDisaggChart
										type='money'
										office='US House'
										topic={DataKey.TotalRaised}
										title='Republican Incumbents'
										chartData={data.miniBar4}
										maxValue={data.miniBar3[3].value}
									/>
								</Box>
								<Typography
									sx={{
										padding: '0 2rem 1rem 0',
										backgroundColor: theme.palette.chartElementsBackground.bg,
										fontFamily: 'Source Sans Pro, sans-serif'
									}}
								>
									MENA - Middle Eastern/North African &nbsp; NatAm - Native
									American/Alaska Native/Native Hawaiian &nbsp; AAPI - Asian
									American/Pacific Islander
								</Typography>
								<Caption
									title={caption.totalRaised.title}
									text={caption.totalRaised.text}
								/>
							</Box>

							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body3' sx={{ margin: '4rem 0 1.5rem 0' }}>
									{contentCopy.congress.content3C}
								</Typography>
							</Box>

							<Box
								sx={{
									backgroundColor: theme.palette.chartElementsBackground.bg,
									padding: { xs: '1rem .5rem', sm: '1rem 3rem' }
								}}
							>
								<Typography
									level='body3'
									sx={{ textAlign: 'center', marginTop: '2rem' }}
								>
									Challengers
								</Typography>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: gridLayout,
										flexGrow: 1
									}}
								>
									<SimpleBarChart
										office='US Senate'
										topic={DataKey.TotalRaised}
										chartData={data.bar3}
										maxValue={data.bar3[0].value} //charts need to be scaled to the same max value and this is the biggest one
									/>
									<SimpleBarChart
										office='US House'
										topic={DataKey.TotalRaised}
										chartData={data.bar4}
										maxValue={data.bar3[0].value}
									/>
								</Box>
								<Caption
									title={caption.totalRaised.title}
									text={caption.totalRaised.text}
								/>
							</Box>

							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body3' sx={{ margin: '4rem 0 1.5rem 0' }}>
									{contentCopy.congress.content3D}
								</Typography>
							</Box>

							<Box
								sx={{
									backgroundColor: theme.palette.chartElementsBackground.bg,
									padding: { xs: '1rem .5rem', sm: '1rem 3rem' }
								}}
							>
								<Typography
									level='body3'
									sx={{ textAlign: 'center', marginTop: '2rem' }}
								>
									Open Seats
								</Typography>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: gridLayout,
										flexGrow: 1
									}}
								>
									<SimpleBarChart
										office='US Senate'
										topic={DataKey.TotalRaised}
										chartData={data.bar5}
										maxValue={data.bar5[0].value} //charts need to be scaled to the same max value and this is the biggest one
									/>
									<SimpleBarChart
										office='US House'
										topic={DataKey.TotalRaised}
										chartData={data.bar6}
										maxValue={data.bar5[0].value}
									/>
								</Box>
								<Caption
									title={caption.totalRaised.title}
									text={caption.totalRaised.text}
								/>
							</Box>

							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body3' sx={{ margin: '4rem 0 1.5rem 0' }}>
									{contentCopy.congress.content3Ea}{' '}
									<i>{contentCopy.congress.content3Eb}</i>{' '}
									<span>{contentCopy.congress.content3Ec}</span>
								</Typography>
								<Typography level='body3' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.congress.content3F}
								</Typography>

								<Stack
									sx={{
										flexDirection: 'row',
										justifyContent: 'center',
										margin: '2rem 0 5rem'
									}}
								>
									<DotsIndicator
										chapter={chapter}
										diameter='29px'
										isActive={true}
									/>
								</Stack>
							</Box>

							{/* STATE */}
							<AnalysisHeading
								number='2'
								title='State Elections'
								anchor='state'
							/>

							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='h4' sx={{ marginTop: '4rem' }}>
									{contentCopy.state.title1}
								</Typography>
								<Typography level='body3' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.state.content1A}
								</Typography>
							</Box>
							<Box
								sx={{
									backgroundColor: theme.palette.chartElementsBackground.bg,

									padding: { xs: '1rem .5rem', sm: '1rem 3rem' }
								}}
							>
								<Typography
									level='body3'
									sx={{ textAlign: 'center', marginTop: '1rem' }}
								>
									Open Seats (woman v. man)
								</Typography>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: gridLayout,
										flexGrow: 1
									}}
								>
									<SimpleRadialChart
										office={'State Senate'}
										topic={DataKey.PropSelf}
										small={!matchesLG}
										chartData={data.stateRadial1}
									/>
									<SimpleRadialChart
										office={'State House'}
										topic={DataKey.PropSelf}
										small={!matchesLG}
										chartData={data.stateRadial2}
									/>
								</Box>
								<Caption
									title={caption.selfFinance.title}
									text={caption.selfFinance.text}
								/>
							</Box>
							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body3' sx={{ margin: '4rem 0 1.5rem 0' }}>
									{contentCopy.state.content1B}
								</Typography>
								<Stack
									sx={{
										flexDirection: 'row',
										justifyContent: 'center',
										margin: '2rem 0 5rem'
									}}
								>
									<DotsIndicator
										chapter={chapter}
										diameter='29px'
										isActive={true}
									/>
								</Stack>
								<Typography level='body3' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.state.content1C}
								</Typography>
							</Box>
							<Box
								sx={{
									backgroundColor: theme.palette.chartElementsBackground.bg,

									padding: { xs: '1rem .5rem', sm: '1rem 3rem' }
								}}
							>
								<Typography
									level='body3'
									sx={{ textAlign: 'center', marginTop: '1rem' }}
								>
									Open Seats (woman v. man)
								</Typography>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: gridLayout,
										flexGrow: 1
									}}
								>
									<SimpleRadialChart
										office={'State Senate'}
										topic={DataKey.PropSmall}
										small={!matchesLG}
										chartData={data.stateRadial3}
									/>
									<SimpleRadialChart
										office={'State House'}
										topic={DataKey.PropSmall}
										small={!matchesLG}
										chartData={data.stateRadial4}
									/>
								</Box>
								<Caption
									title={caption.contrib.title}
									text={caption.contrib.text}
								/>
							</Box>
							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body3' sx={{ margin: '4rem 0 1.5rem 0' }}>
									{contentCopy.state.content1D}
								</Typography>
								<Typography level='body3' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.state.content1E}
								</Typography>
								<Stack
									sx={{
										flexDirection: 'row',
										justifyContent: 'center',
										margin: '2rem 0 5rem'
									}}
								>
									<DotsIndicator
										chapter={chapter}
										diameter='29px'
										isActive={true}
									/>
								</Stack>
								<Typography level='h4' sx={{ margin: '4rem 0 1.5rem 0' }}>
									{contentCopy.state.title2}
								</Typography>
								<Typography level='body3' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.state.content2A}
								</Typography>
								<Typography level='body3' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.state.content2B}
								</Typography>
								<Typography level='body3' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.state.content2C}
								</Typography>
								<Stack
									sx={{
										flexDirection: 'column',
										alignItems: 'center',
										margin: '2rem 0'
									}}
								>
									<DotsIndicator
										chapter={chapter}
										diameter='29px'
										isActive={true}
									/>
									<Link
										href={
											'/modules/custom/cawp_wmpw/assets/pdfs/CAWP_Final_Analysis_Congressional_and_State_Elections.pdf'
										}
										sx={{
											marginTop: '2.5rem',
											marginBottom: '.5rem',
											':hover': { textDecoration: 'none' }
										}}
										download='CAWP_Final_Analysis_Congressional_and_State_Elections.pdf'
									>
										<Button
											variant='outlined'
											color='neutral'
											sx={{
												':hover': {
													color: '#FFFFFF',
													'& .MuiSvgIcon-root': {
														color: '#FFFFFF'
													}
												},

												padding: '10px',
												paddingTop: '11px',
												paddingX: '15px',

												color: '#111827',
												fontWeight: 'normal',
												fontSize: '20px',
												border: '1.5px solid #374151',
												'.MuiSvgIcon-root': {
													fontSize: '1.75rem',
													color: '#111827',
													marginRight: '0.25rem'
												}
											}}
											// sx={{
											// 	':hover': {
											// 		backgroundImage: `linear-gradient(to right, #A2263D, #58274F, #0E255E , #0E255F, #0E2560)`,
											// 		textDecoration: 'none'
											// 	},
											// 	backgroundColor: theme.palette.controls.controlFill,
											// 	padding: '10px',
											// 	paddingTop: '11px',
											// 	paddingX: '45px',
											// 	margin: '2.5rem 0',
											// 	'.MuiSvgIcon-root': {
											// 		fontSize: '1.75rem'
											// 	}
											// }}
										>
											<Download />
											{/* <Typography
											level='label'
											sx={{
												marginLeft: '.5rem',
												fontWeight: 'normal'
											}}
										> */}
											Download PDF Report
											{/* </Typography> */}
										</Button>
									</Link>
								</Stack>
							</Box>

							<Divider width={'calc(100% + 1.2rem)'} />
							<OtherPages currentChapter='analysis' />
						</Box>
					</Content>
					<Content></Content>
					<Content>
						<Box sx={{ maxWidth: { xs: '100%', lg: 1050 } }}>
							<AnalysisHeading number='3' title='Methodology' />
							<Box sx={{ margin: '0 auto', maxWidth: '824px', width: '100%' }}>
								<Typography level='body1' sx={{ marginTop: '4rem' }}>
									{contentCopy.methodology.content1A}
								</Typography>
								<Typography level='body1' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.methodology.content1B}
								</Typography>
								<Typography level='body1' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.methodology.content1C}
								</Typography>
								<Typography level='body1' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.methodology.content1D}
								</Typography>
								<Typography level='body1' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.methodology.content1E}
								</Typography>
								<Typography level='body1' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.methodology.content1F}
								</Typography>
								<Typography level='body1' sx={{ margin: '1.5rem 0' }}>
									{contentCopy.methodology.content1G}
								</Typography>
							</Box>
						</Box>
					</Content>
					<SimpleNav />
				</Box>
			</Box>
		</PageWrapper>
	);
};

export default AnalysisPage;
